// features/Languages/languageSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/api';

// Async Thunks
export const fetchLanguages = createAsyncThunk(
  'languages/fetchLanguages',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/languages');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch languages');
    }
  }
);

export const createLanguage = createAsyncThunk(
  'languages/createLanguage',
  async (languageData, { rejectWithValue }) => {
    try {
      const response = await api.post('/languages', languageData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create language');
    }
  }
);

export const updateLanguage = createAsyncThunk(
  'languages/updateLanguage',
  async ({ id, ...languageData }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/languages/${id}`, {
        ...languageData,
        _method: 'PATCH', // Laravel compatibility
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Update failed');
    }
  }
);

export const deleteLanguage = createAsyncThunk(
  'languages/deleteLanguage',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/languages/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Delete failed');
    }
  }
);

// Slice
const languageSlice = createSlice({
  name: 'languages',
  initialState: {
    languages: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Languages
      .addCase(fetchLanguages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLanguages.fulfilled, (state, action) => {
        state.loading = false;
        state.languages = action.payload.data;
      })
      .addCase(fetchLanguages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create Language
      .addCase(createLanguage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createLanguage.fulfilled, (state, action) => {
        state.loading = false;
        state.languages.push(action.payload);
      })
      .addCase(createLanguage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Language
      .addCase(updateLanguage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLanguage.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.languages.findIndex((lang) => lang.id === action.payload.id);
        if (index !== -1) {
          state.languages[index] = action.payload;
        }
      })
      .addCase(updateLanguage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Language
      .addCase(deleteLanguage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteLanguage.fulfilled, (state, action) => {
        state.loading = false;
        state.languages = state.languages.filter((lang) => lang.id !== action.payload);
      })
      .addCase(deleteLanguage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default languageSlice.reducer;